import React, { type PropsWithChildren, useCallback, useMemo } from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl-next';
import Modal from '@atlaskit/modal-dialog';
import Button, { IconButton, LinkButton } from '@atlaskit/button/new';
import ShortcutIcon from '@atlaskit/icon/core/link-external';
import CrossIcon from '@atlaskit/icon/core/close';
import Tooltip from '@atlaskit/tooltip';
import messages from './messages';
import {
	ModalBodyViewWrapper,
	ModalBodyEditWrapper,
	ModalContainer,
	CustomModalHeader,
	ModalInnerBlanket,
	ModalOuterBlanket,
} from './styled';
import { type EmbeddedDialogMode } from '../../../containers/embedded-page/types';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import OriginTracing from '@atlassiansox/origin-tracing';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

type Props = PropsWithChildren<
	{
		mode: EmbeddedDialogMode;
		headerBtnHref?: string;
		onClose: () => void;
		isInnerModalOpen: boolean;
	} & WrappedComponentProps
>;

const EmbeddedPageModal = ({
	mode,
	headerBtnHref,
	onClose,
	children,
	isInnerModalOpen,
	intl: { formatMessage },
}: Props) => {
	const Header = () => {
		const origin = useMemo(() => new OriginTracing({ product: 'jira' }), []);
		const handleOpenInConfluence = useCallback(
			(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
				const originAttributes = origin?.toAnalyticsAttributes({
					hasGeneratedId: true,
				});
				fireUIAnalytics(analyticsEvent, 'openEmbeddedViewPageInConfluenceButton', originAttributes);
			},
			[origin],
		);
		return (
			<CustomModalHeader>
				{headerBtnHref ? (
					<LinkButton
						iconAfter={ShortcutIcon}
						// technically href already has an origin generated by confluence-table-tree, but we clobber it
						// since we are firing the click event with a new generated id
						href={headerBtnHref}
						target="_blank"
						onClick={handleOpenInConfluence}
						testId="confluence-project-pages.embedded-confluence-dialog.open-in-confluence-button"
					>
						{formatMessage(messages.titleButton)}
					</LinkButton>
				) : (
					<Button
						iconAfter={ShortcutIcon}
						onClick={handleOpenInConfluence}
						testId="confluence-project-pages.embedded-confluence-dialog.open-in-confluence-button"
					>
						{formatMessage(messages.titleButton)}
					</Button>
				)}
				<Tooltip content={formatMessage(messages.closeHint)}>
					<IconButton
						onClick={onClose}
						appearance="subtle"
						icon={CrossIcon}
						label={formatMessage(messages.closeHint)}
						testId="confluence-project-pages.embedded-confluence-dialog.close-button"
					/>
				</Tooltip>
			</CustomModalHeader>
		);
	};
	const modalBodyWrapperTestId =
		mode === 'view'
			? 'confluence-project-pages.embedded-confluence-dialog.modal-body-view-wrapper'
			: 'confluence-project-pages.embedded-confluence-dialog.modal-body-edit-wrapper';
	const ModalBodyWrapper = mode === 'view' ? ModalBodyViewWrapper : ModalBodyEditWrapper;
	return (
		<>
			<Modal autoFocus={false} onClose={onClose} width={'100%'} height={'100%'}>
				<ModalContainer>
					{mode === 'view' && <Header />}
					<ModalBodyWrapper data-testid={modalBodyWrapperTestId}>
						{children}
						{isInnerModalOpen && (
							<ModalInnerBlanket data-testid="confluence-project-pages.embedded-confluence-dialog.modal-inner-blanket" />
						)}
					</ModalBodyWrapper>
				</ModalContainer>
			</Modal>
			{isInnerModalOpen && (
				<ModalOuterBlanket data-testid="confluence-project-pages.embedded-confluence-dialog.modal-outer-blanket" />
			)}
		</>
	);
};

export default injectIntl<'intl', WrappedComponentProps & Props>(EmbeddedPageModal);
